import React, { Component, Fragment } from 'react';
import moment from 'moment';
import withStyles from '@mui/styles/withStyles';
import IconButton from '@mui/material/IconButton';
import Typography from '../../components/form/Typography';
import TextField from '../../components/form/TextField';
import Checkbox from '../../components/form/Checkbox';
import ColumnLayout from '../../components/layout/ColumnLayout';
import Grid from '../../components/layout/Grid';
import Inline, { inlineAlignment, InlineField } from '../../components/layout/Inline';
import Button from '../../components/form/Button';
import AshLocationHistoryModal from '../../components/modal/AshLocationHistoryModal';
import Select from '../../components/form/Select';
import AlertModal from '../../components/modal/AlertModal';
import { GenderOptions, MaritalStatusOptions } from '../../components/ComponentConstants';
import CremationOrderFragment from '../../fragments/CremationOrder';
import AddressAutocomplete, { createAddressComponentFields } from '../../components/form/AddressAutocomplete';
import { numberToWords } from '../../utils/strings';
import { niceDateFromString, niceTimeFromString } from '../../utils/date';
import RadioGroup from '../../components/form/RadioGroup';
import Table, { Cell, HeaderRow, Row } from '../../components/table/Table';
import ReflectionRoomBookingModal from './Modals/ReflectionRoomBookingModal';
import ReadonlyField from '../../components/form/ReadonlyField';
import StaffAllocationTable from '../../components/form/StaffAllocationTable';
import { DeleteIcon, EditIcon, TickCircleIcon } from '../../components/IconIndex';
import Dispatch from '../../components/modal/Dispatch';
import Pickup, { PickupOrDeliveryField } from '../../components/pickup/Pickup';

const deceasedAddressComponentFields = createAddressComponentFields('Deceased.ResidentialAddress');

class RecordDetailsTab extends Component {
    state = {
        showAshLocationHistoryModal: false,
        showDeleteInstructionAlertModal: false,
        showDeleteAshLocationAlertModal: false,
        showReflectionRoomBookingModal: false,
        showDeleteReflectionRoomBookingAlertModal: false,
        editAshLocationHistoryIndex: null,
        specialInstructionToDelete: null,
        readonlyDeceasedDetails: true
    };

    static getDerivedStateFromProps(newProps, oldState) {
        const { form } = newProps;
        const { currentRecordID } = oldState;
        if (!currentRecordID && form) {
            const setRecordID = form.getField('ID');
            const funeralID = form.getField('Funeral.ID');
            return ({
                currentRecordID: setRecordID,
                readonlyDeceasedDetails: !!funeralID
            });
        }
        return null;
    }

    render() {
        const { showAshLocationHistoryModal } = this.state;
        const { showDeleteInstructionAlertModal } = this.state;
        const { showDeleteAshLocationAlertModal } = this.state;
        const { showReflectionRoomBookingModal } = this.state;
        const { showDeleteReflectionRoomBookingAlertModal } = this.state;
        const { editAshLocationHistoryIndex } = this.state;
        const { form } = this.props;

        let editAshLocationHistory = {
            ID: null,
            Location: '',
            LocationDetail: '',
            Notes: '',
            Changes: []
        };

        if (editAshLocationHistoryIndex !== null && editAshLocationHistoryIndex !== undefined) {
            editAshLocationHistory = form.getState('AshLocations')[editAshLocationHistoryIndex];
        }

        return (
            <Fragment>
                <ColumnLayout>
                    {this.renderLeftColumn()}
                    {this.renderRightColumn()}
                </ColumnLayout>

                <AshLocationHistoryModal
                    open={showAshLocationHistoryModal}
                    onClose={() => this.handleCloseAshLocationHistoryModal()}
                    onCancel={() => this.handleCloseAshLocationHistoryModal()}
                    onSubmit={ashLocationHistory => this.handleSaveAshLocationHistoryModal(ashLocationHistory)}
                    ashLocationHistory={editAshLocationHistory}
                    name="editAshLocationHistory"
                />

                <ReflectionRoomBookingModal
                    open={showReflectionRoomBookingModal}
                    data={form.getField('ReflectionRoom')}
                    onClose={newItem => this.handleCloseReflectionRoomBookingModal(newItem)}
                />

                <AlertModal
                    variant="warning"
                    open={showDeleteReflectionRoomBookingAlertModal}
                    title="Prompt"
                    secondaryAction="Yes, Delete"
                    onClickSecondaryAction={() => this.handleDeleteReflectionRoomBooking()}
                    primaryAction="Cancel"
                    onClickPrimaryAction={() => this.handleCloseDeleteReflectionRoomBookingAlertModal()}
                    onClose={() => this.handleCloseDeleteReflectionRoomBookingAlertModal()}
                >
                    <p>You're about to delete this viewing room booking.</p>
                    <p>Are you sure?</p>
                </AlertModal>

                <AlertModal
                    variant="warning"
                    open={showDeleteInstructionAlertModal}
                    title="Prompt"
                    secondaryAction="Yes, Delete"
                    onClickSecondaryAction={() => this.handleDeleteSpecialInstruction()}
                    primaryAction="Cancel"
                    onClickPrimaryAction={() => this.handleCloseDeleteSpecialInstructionAlertModal()}
                    onClose={() => this.handleCloseDeleteSpecialInstructionAlertModal()}
                >
                    <p>You're about to delete this special instruction.</p>
                    <p>Are you sure?</p>
                </AlertModal>

                <AlertModal
                    variant="warning"
                    open={showDeleteAshLocationAlertModal}
                    title="Prompt"
                    secondaryAction="Yes, Delete"
                    onClickSecondaryAction={() => this.handleDeleteAshLocationHistory()}
                    primaryAction="Cancel"
                    onClickPrimaryAction={() => this.handleCloseDeleteAshLocationAlertModal()}
                    onClose={() => this.handleCloseDeleteAshLocationAlertModal()}
                >
                    <p>You're about to delete this ash location history.</p>
                    <p>Are you sure?</p>
                </AlertModal>
            </Fragment>
        );
    }

    renderLeftColumn() {
        return (
            <Fragment>
                {this.renderDeceasedDetails()}
                {this.renderBookingRequest()}
                {this.renderBookingOptions()}
            </Fragment>
        );
    }

    renderDeceasedDetails() {
        const { form } = this.props;
        const { readonlyDeceasedDetails } = this.state;
        const funeralID = form.getField('Funeral.ID');
        const hadPacemaker = !!form.getField('HadPacemaker');
        const checkPacemaker = (evt, val) => !val && form.setField({ HadPacemakerRemoved: false });
        return (
            <Grid container bucket>
                <Grid item>
                    <Inline alignment={inlineAlignment.rightAlignSiblings}>
                        <h4>Deceased details</h4>
                        {!!funeralID && <IconButton
                            className="icon"
                            title={readonlyDeceasedDetails ? 'Edit' : 'Accept'}
                            onClick={() => this.setState({ readonlyDeceasedDetails: !readonlyDeceasedDetails })} size="large">
                            {readonlyDeceasedDetails ? <EditIcon /> : <TickCircleIcon />}
                        </IconButton>}
                    </Inline>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="First name"
                        form={form}
                        name="Deceased.FirstName"
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Middle name(s)"
                        form={form}
                        name="Deceased.MiddleName"
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField label="Surname" form={form} name="Deceased.Surname" readOnly={readonlyDeceasedDetails} />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Select
                        label="Gender"
                        options={GenderOptions}
                        form={form}
                        name="Deceased.Gender"
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Date of Birth"
                        type="date"
                        form={form}
                        name="Deceased.DateOfBirth"
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Date of Death"
                        type="date"
                        form={form}
                        name="Deceased.DateOfDeath1"
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Select
                        label="Marital Status"
                        options={MaritalStatusOptions}
                        form={form}
                        name="Deceased.MaritalStatus"
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12}>
                    <AddressAutocomplete
                        allowCustomAddress
                        label="Residential Address"
                        placeholder="Search for residential address..."
                        form={form}
                        componentFields={deceasedAddressComponentFields}
                        readOnly={readonlyDeceasedDetails}
                    />
                </Grid>

                <Grid item xs={12}>
                    <InlineField>
                        <Checkbox
                            form={form}
                            name="HadPacemaker"
                            label="Internal Pacemaker"
                            readOnly={readonlyDeceasedDetails}
                            onChange={checkPacemaker} />
                        {hadPacemaker && (
                            <Checkbox
                                form={form}
                                name="HadPacemakerRemoved"
                                label="Pacemaker Removed"
                                readOnly={readonlyDeceasedDetails}
                            />
                        )}
                    </InlineField>
                </Grid>
            </Grid>
        );
    }

    renderBookingRequest() {
        const { form } = this.props;
        const funeralID = form.getField('Funeral.ID');
        const funeralService = form.getField('FuneralService') || form.getField('Funeral.PlaceOfService.Type') || '';
        const serviceOptions = [
            { label: 'No Service', value: 'No Service No Attendance' },
            { label: 'Full Service', value: 'Service' },
            { label: 'Unsure', value: null }
        ];
        const idx = serviceOptions.findIndex(obj => obj.value === funeralService);
        if (funeralService && idx < 0) {
            serviceOptions.push({ label: funeralService, value: funeralService });
        }
        return (
            <Grid container bucket>
                <Grid item>
                    <Inline alignment={inlineAlignment.rightAlignSiblings} center>
                        <h4>{!!funeralID ? 'Booking Request' : 'Booking'}</h4>
                    </Inline>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label={(!!funeralID ? 'Requested ' : '') + 'Arrival Date'}
                        type="date"
                        form={form}
                        name="CremationDate"
                        readOnly={!!funeralID}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label={(!!funeralID ? 'Requested ' : '') + 'Arrival Time'}
                        type="time"
                        form={form}
                        name="CremationTime"
                        readOnly={!!funeralID}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Select
                        label="Funeral Service Type"
                        form={form}
                        name="FuneralService"
                        options={serviceOptions}
                        value={funeralService}
                        readOnly={!!funeralID}
                        onChange={(evt, val) => {
                            if (val) {
                                form.setField({ 'IsDeliveryOnly': 'No Service No Attendance' === val });
                            } else {
                                form.setField({ 'IsDeliveryOnly': form.getOriginal('IsDeliveryOnly') });
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Checkbox
                        label="Same-day Cremation"
                        form={form}
                        name="SameDayCremation"
                        readOnly={!!funeralID}
                        helperText="As required by customer."
                    />
                </Grid>
            </Grid>
        );
    }

    renderBookingOptions() {
        const { form } = this.props;
        const funeralID = form.getField('Funeral.ID');
        return (
            <Grid container>
                <Grid container bucket>
                    <Grid item>
                        <Inline alignment={inlineAlignment.rightAlignSiblings} center>
                            <h4>Booking Options</h4>
                        </Inline>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Checkbox
                            label="Request for Certificate of Contents"
                            form={form}
                            name="RequestsCertificateOfContents"
                            readOnly={!!funeralID}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Checkbox
                            label="Priority Cremation"
                            form={form}
                            name="NextDayCremation"
                            helperText="As required by staff."
                        />
                    </Grid>
                    {this.renderWitnessOfInsertionRequired(false)}
                    {this.renderReflectionRoomRequired(false)}
                    {this.renderReflectionRoom(false)}
                    <Grid item xs={12} sm={6}>
                        <PickupOrDeliveryField form={form}/>
                    </Grid>
                </Grid>
                <Pickup form={form} />
            </Grid>
        );
    }

    renderSpecialInstructions() {
        const { form } = this.props;
        const specialInstructions = form.getState('SpecialInstructions') || [];
        return (
            <Grid container bucket>
                <Grid item>
                    <h4>Special Instructions</h4>
                </Grid>

                {specialInstructions.length > 0 &&
                    specialInstructions.map((specialInstruction, index) => (
                        <Grid itemBucket key={index}>
                            <Grid variant="justify" item>
                                <h5>Instruction {numberToWords(index + 1)}</h5>
                                <Inline center alignment={inlineAlignment.rightAlignSiblings}>
                                    <div>
                                        <TextField form={form} name={`SpecialInstructions[${index}].Title`} />
                                    </div>
                                    <IconButton
                                        className="icon"
                                        title="Delete"
                                        onClick={() => this.handleShowDeleteSpecialInstructionAlertModal(index)} size="large">
                                        <DeleteIcon />
                                    </IconButton>
                                </Inline>
                            </Grid>
                            <Grid variant="justify" item>
                                <Inline alignment={inlineAlignment.rightAlignSiblings} center className="nowrap">
                                    <div className="instruction">
                                        <TextField
                                            label="Notes"
                                            form={form}
                                            name={`SpecialInstructions[${index}].Comment`}
                                            multiline
                                            button={{ children: '+ Add Notes' }}
                                        />
                                    </div>
                                    <Checkbox
                                        label="Confirmed"
                                        form={form}
                                        name={`SpecialInstructions[${index}].Confirmed`}
                                    />

                                </Inline>
                            </Grid></Grid>
                    ))}

                <Grid item>
                    <Button variant="primary"
                            onClick={() => this.handleAddSpecialInstruction()}>
                        + Add Special Instruction
                    </Button>
                </Grid>
            </Grid>
        );
    }

    renderAshLocation() {
        const { form } = this.props;
        const ashLocations = form.getState('AshLocations') || [];
        return (
            <Grid container bucket>
                <Grid item>
                    <h4>Ash Location</h4>
                </Grid>

                {ashLocations.length > 0 &&
                    ashLocations.map((ashLocation, index) => (
                        <Grid item key={ashLocation.ID}>
                            <Inline nowrap alignment={inlineAlignment.rightAlignSiblings} center>
                                <div>
                                    <Typography component="p">
                                        {ashLocation.Location}
                                        {ashLocation.Ashes && ` (${ashLocation.Ashes})`}
                                    </Typography>
                                    <Typography component="p"><small>{ashLocation.LocationDetail}</small></Typography>
                                </div>
                                <IconButton
                                    className="icon"
                                    onClick={() => this.handleShowAshLocationHistoryModal(index)} size="large">
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    className="icon"
                                    onClick={() => this.handleShowDeleteAshLocationAlertModal()} size="large">
                                    <DeleteIcon />
                                </IconButton>
                                {/*<IconButton className="icon" onClick={() => this.handleDeleteAshLocationHistory(index)} size="large"><DeleteIcon /></IconButton>*/}
                            </Inline>
                        </Grid>
                    ))}

                <Grid item>
                    <Button variant="primary" onClick={() => this.handleShowAshLocationHistoryModal()}>
                        Log Ash Location...
                    </Button>
                </Grid>
            </Grid>
        );
    }

    renderRightColumn() {
        const { form } = this.props;
        return (
            <Fragment>
                {this.renderAshLocation()}
                <Dispatch form={form} />
                {this.renderSpecialInstructions()}
            </Fragment>
        );
    }

    renderReflectionRoomRequired(readOnly) {
        const { form } = this.props;
        return (
            <Fragment>
                <Grid item xs={12} sm={6}>
                    {!readOnly ? (
                        <RadioGroup
                            label="Viewing Room"
                            options={[
                                { label: 'Yes', value: 'Required' },
                                { label: 'No', value: 'NotRequired' },
                                { label: 'Unsure', value: '' }
                            ]}
                            name="ReflectionRoomRequired"
                            disabled={readOnly}
                            form={form}
                        />
                    ) : (
                        <ReadonlyField label="Viewing Room">
                            {'Required' === form.getField('ReflectionRoomRequired') ? 'Yes' : 'No'}
                        </ReadonlyField>
                    )}
                </Grid>
            </Fragment>
        );
    }

    renderReflectionRoom(readOnly) {
        const { form } = this.props;
        const showBookingTable = form.getField('ReflectionRoomRequired') === 'Required';
        if (!showBookingTable) return null;

        const ReflectionRoom = form.getField('ReflectionRoom');
        const HasReflectionRoom = ReflectionRoom && ReflectionRoom.Date;
        const formattedNames =
            HasReflectionRoom && ReflectionRoom.AttendeeNames && ReflectionRoom.AttendeeNames.replace(/\n/g, ', ');

        const dateStamp = moment(form.getField('CremationDate') || '').format('YYYYMMDD');

        return (
            <Fragment>
                <Grid item>
                    <h5>Viewing Room Booking</h5>
                </Grid>

                {showBookingTable && (
                    <Fragment>
                        {HasReflectionRoom && (<>
                            <Grid item>
                                <Table>
                                    <HeaderRow pad>
                                        <Cell colSpan={1}>Date</Cell>
                                        <Cell colSpan={1}>Time</Cell>
                                        <Cell colSpan={1}>Attendees & notes</Cell>
                                        {!readOnly && <Cell colSpan={2}>Edit/Delete</Cell>}
                                    </HeaderRow>
                                    <Row pad>
                                        <Cell dataLabel="Date">
                                            {niceDateFromString(ReflectionRoom.Date)}
                                        </Cell>
                                        <Cell dataLabel="Time">
                                            {niceTimeFromString(ReflectionRoom.Start)}
                                            {' to '}
                                            {niceTimeFromString(ReflectionRoom.End)}
                                        </Cell>
                                        <Cell dataLabel="Attendees">{formattedNames}</Cell>
                                        {(!readOnly) && (
                                            <Cell dataLabel="Edit/Delete" pad={false} tiny>
                                                <div style={{
                                                    display: 'flex',
                                                    flexWrap: 'nowrap',
                                                    justifyContent: 'space-between'
                                                }}>
                                                    <IconButton
                                                        className="icon"
                                                        onClick={() => this.handleShowReflectionRoomBookingModal()} size="large">
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => !!ReflectionRoom.ID
                                                            ? this.handleShowDeleteReflectionRoomBookingAlertModal()
                                                            : this.handleDeleteReflectionRoomBooking()
                                                        }
                                                        className="icon" size="large">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                            </Cell>
                                        )}
                                    </Row>
                                </Table>
                            </Grid>
                            <StaffAllocationTable
                                form={form}
                                name="ReflectionRoom.StaffAllocations"
                                allocationType="ReflectionRoom"
                                defaultDate="ReflectionRoom.Date"
                                defaultTime="ReflectionRoom.Time"
                                defaultDuration="ReflectionRoom.Duration"
                                readOnly={readOnly}
                            />
                        </>)}
                        <Grid item>
                            <Inline>
                                {!HasReflectionRoom && (!readOnly ? (
                                    <Button
                                        variant="primary"
                                        onClick={() => this.handleShowReflectionRoomBookingModal()}
                                    >
                                        Add Room Booking...
                                    </Button>
                                ) : (<ReadonlyField>MISSING!</ReadonlyField>))}
                                <Button variant="secondary" href={'/calendar/' + dateStamp} target="_blank">
                                    View Calendar
                                </Button>
                            </Inline>
                        </Grid>
                    </Fragment>
                )}
            </Fragment>
        );
    }

    renderWitnessOfInsertionRequired(readOnly) {
        const { form } = this.props;
        return (
            <Fragment>
                <Grid item xs={12} sm={6}>
                    {!readOnly ? (
                        <RadioGroup
                            label="Witness of Insertion"
                            options={[
                                { label: 'Yes', value: 'Required' },
                                { label: 'No', value: 'NotRequired' },
                                { label: 'Unsure', value: '' }
                            ]}
                            name="WitnessOfInsertionRequired"
                            disabled={readOnly}
                            form={form}
                        />
                    ) : (
                        <ReadonlyField label="Witness of Insertion">
                            {'Required' === form.getField('WitnessOfInsertionRequired') ? 'Yes' : 'No'}
                        </ReadonlyField>
                    )}
                </Grid>
            </Fragment>
        );
    }


    handleDeleteReflectionRoomBooking() {
        const { form } = this.props;

        form.setState({ ReflectionRoom: null });
    }

    handleShowReflectionRoomBookingModal() {
        this.setState({ showReflectionRoomBookingModal: true });
    }

    handleCloseReflectionRoomBookingModal(newItem) {
        const { form } = this.props;
        this.setState({ showReflectionRoomBookingModal: false });
        if (newItem) form.setField({ ReflectionRoom: newItem });
    }

    handleShowDeleteReflectionRoomBookingAlertModal() {
        this.setState({ showDeleteReflectionRoomBookingAlertModal: true });
    }

    handleCloseDeleteReflectionRoomBookingAlertModal() {
        this.setState({ showDeleteReflectionRoomBookingAlertModal: false });
    }

    handleShowAshLocationHistoryModal(index) {
        this.setState({ editAshLocationHistoryIndex: index });
        this.setState({ showAshLocationHistoryModal: true });
    }

    handleCloseAshLocationHistoryModal() {
        this.setState({ showAshLocationHistoryModal: false });
        this.setState({ editAshLocationHistoryIndex: null });
    }

    handleSaveAshLocationHistoryModal(ashLocation) {
        const { form } = this.props;
        const { editAshLocationHistoryIndex } = this.state;
        const AshLocations = form.getState('AshLocations') || [];

        if (
            editAshLocationHistoryIndex !== null &&
            editAshLocationHistoryIndex !== undefined &&
            AshLocations[editAshLocationHistoryIndex]
        ) {
            const existingAshLocationHistory = AshLocations[editAshLocationHistoryIndex];
            Object.assign(existingAshLocationHistory, ashLocation);
        } else {
            AshLocations.push(ashLocation);
        }

        this.setState({ showAshLocationHistoryModal: false });
        this.setState({ editAshLocationHistoryIndex: null });

        form.setState({ AshLocations });
        form.save();
    }

    handleDeleteAshLocationHistory(index) {
        const { form } = this.props;
        const AshLocations = form.getState('AshLocations') || [];

        if (index !== -1) {
            AshLocations.splice(index, 1);
            form.setState({ AshLocations });
        }
        this.handleCloseDeleteAshLocationAlertModal();
    }

    handleAddSpecialInstruction() {
        const { form } = this.props;
        const SpecialInstructions = form.getState('SpecialInstructions') || [];
        SpecialInstructions.push({ Title: '', Comment: '', Confirmed: false });
        form.setState({ SpecialInstructions });
    }

    handleDeleteSpecialInstruction() {
        const { form } = this.props;
        const { specialInstructionToDelete } = this.state;
        const SpecialInstructions = form.getState('SpecialInstructions') || [];
        if (specialInstructionToDelete !== null) {
            SpecialInstructions.splice(specialInstructionToDelete, 1);
            form.setState({ SpecialInstructions });
        }
        this.handleCloseDeleteSpecialInstructionAlertModal();
    }

    handleShowDeleteSpecialInstructionAlertModal(index) {
        this.setState({ showDeleteInstructionAlertModal: true, specialInstructionToDelete: index });
    }

    handleCloseDeleteSpecialInstructionAlertModal() {
        this.setState({ showDeleteInstructionAlertModal: false, specialInstructionToDelete: null });
    }

    handleShowDeleteAshLocationAlertModal() {
        this.setState({ showDeleteAshLocationAlertModal: true });
    }

    handleCloseDeleteAshLocationAlertModal() {
        this.setState({ showDeleteAshLocationAlertModal: false });
    }
}

export default {
    id: 'RecordDetails',
    label: 'Record Details',
    component: withStyles({})(RecordDetailsTab),
    fragment: CremationOrderFragment,
    validation: {
        required: ['Deceased.FirstName']
    },
    onLoad: data => {
        if (data.ReflectionRoomRequired && data.ReflectionRoomRequired !== 'Required' && !data.ReflectionRoom) {
            data.ReflectionRoom = { ID: 0 };
        }
        if (!data.FuneralService) {
            data.FuneralService = ''; // need an empty string for the dropdown
        }
    },
    formatSaveData: (saveData, state) => {
        if (state.ReflectionRoomRequired !== 'Required' && state.ReflectionRoom) {
            delete saveData.ReflectionRoom;
            delete state.ReflectionRoom;
        }
        if (saveData.DispatchDates) {
            saveData.DispatchDates = state.DispatchDates.map(obj => ({
                ...obj,
                AssignedStaff: obj.AssignedStaff ? obj.AssignedStaff.map(staff => ({ ID: staff.ID })) : []
            }));
        }
        if (saveData.ReflectionRoom && saveData.ReflectionRoom.StaffAllocations) {
            saveData.ReflectionRoom.StaffAllocations = state.ReflectionRoom.StaffAllocations.map(obj => {
                const newObj = { ...obj, MemberID: (obj.Member && obj.Member.ID) || obj.MemberID || null };
                delete newObj.Member;
                return newObj;
            });
        }
    }
};
